import { projectTimezone } from '@dashboard/utils/projectTimezone'
import { useQuery } from '@tanstack/vue-query'
import type { QueryClient } from '@tanstack/vue-query'
import { AcademicCapIcon, CursorArrowRaysIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import { parseOnboardingAiContext } from '@dashboard/modules/onboarding/services/activationResearchContext'
import { createSharedComposable } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import api from '@dashboard/services/axiosApi'
import { observeResource } from '@dashboard/services/realtimeResourceObserver'
import { createRealtimeQueryReconciler } from '@dashboard/services/realtimeQueryReconciler'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import type { DashboardHealth } from '../types/overview'

export type SetupKind = 'survey' | 'quiz' | 'ctas'

export interface SetupDraft {
  id: string
  name: string
  kind: SetupKind | null
  /** Set when this draft was built by an onboarding playbook. */
  playbook?: 'subscriberSurvey' | 'leadGenQuiz' | 'smartCtaFunnel' | null
  questions: number
  nextSteps: number
  live: boolean
}

export const SETUP_COPY = {
  survey: { noun: 'survey', verb: 'is', audience: 'new subscribers', pronoun: 'it', icon: QuestionMarkCircleIcon },
  quiz: { noun: 'quiz', verb: 'is', audience: 'visitors', pronoun: 'it', icon: AcademicCapIcon },
  ctas: { noun: 'smart CTAs', verb: 'are', audience: 'visitors', pronoun: 'them', icon: CursorArrowRaysIcon }
} as const

const PLAYBOOK_KIND = { subscriberSurvey: 'survey', leadGenQuiz: 'quiz', smartCtaFunnel: 'ctas' } as const

export function dashboardSetupKind(draft: SetupDraft | null, researchContext: unknown): SetupKind {
  const key = parseOnboardingAiContext(researchContext).recommendedPlaybook?.key
  return draft?.kind ?? (key ? PLAYBOOK_KIND[key] : 'survey')
}

export function setupDomain(website: string | null | undefined): string {
  if (!website) {
    return 'your website'
  }
  try {
    return new URL(website.includes('://') ? website : `https://${website}`).hostname
  } catch {
    return website
  }
}

export interface DashboardSetupSignals {
  snippetConfirmed: boolean | null
  espConnected: boolean
  flowLive: boolean
  draft: SetupDraft | null
  previewedAt: string | null
  health: DashboardHealth
}

export interface DashboardSetupStep {
  id: 'draft' | 'preview' | 'site' | 'email' | 'publish'
  optional: boolean
  completed: boolean | null
}

export function dashboardSetupSteps(signals: DashboardSetupSignals): DashboardSetupStep[] {
  return [
    { id: 'draft', optional: false, completed: signals.draft !== null },
    // Installing the script is what makes anything reach the site, so it is required; previewing is a check, not a step.
    { id: 'site', optional: false, completed: signals.snippetConfirmed },
    { id: 'email', optional: true, completed: signals.espConnected },
    { id: 'preview', optional: true, completed: signals.previewedAt !== null },
    { id: 'publish', optional: false, completed: signals.flowLive }
  ]
}

export async function recordFlowPreview(pid: number | string | null, queryClient: QueryClient): Promise<void> {
  if (!pid || pid === 'demo') {
    return
  }
  try {
    await api.post(`/api/${pid}/dashboard/setup/preview`)
    await queryClient.invalidateQueries({ queryKey: ['dashboard-setup', pid] })
  } catch {
    return
  }
}

export const useDashboardSetup = createSharedComposable(() => {
  const { currentTeamPid, fullResponse } = storeToRefs(useConfigStore())
  const { integratedPartner, isLoading: integrationsLoading } = storeToRefs(useIntegrationStore())
  // Shared across project switches, so the timezone is read per project rather than once.
  const timezone = computed(() => projectTimezone())
  const query = useQuery({
    queryKey: computed(() => ['dashboard-setup', currentTeamPid.value, timezone.value]),
    queryFn: async () => {
      const response = await api.get<DashboardSetupSignals>(`/api/${currentTeamPid.value}/dashboard/setup`, {
        params: { timezone: timezone.value }
      })
      return response.data
    },
    enabled: computed(
      () =>
        !!currentTeamPid.value && currentTeamPid.value !== 'demo' && !!fullResponse.value && !integrationsLoading.value
    ),
    staleTime: 30_000,
    retry: false
  })
  const reconcile = createRealtimeQueryReconciler(
    () => query.isFetching.value,
    () => query.refetch()
  )
  watch([fullResponse, integratedPartner], () => {
    if (currentTeamPid.value && currentTeamPid.value !== 'demo' && query.data.value) {
      void reconcile()
    }
  })
  watch(
    currentTeamPid,
    (pid, _, onCleanup) => {
      if (!pid || pid === 'demo') {
        return
      }
      const controller = new AbortController()
      let subscribed = false
      observeResource(
        String(pid),
        'analytics.last-received',
        (invalidation) => {
          if (invalidation.source === 'subscription' && !subscribed) {
            subscribed = true
            return
          }
          void reconcile()
        },
        controller.signal
      )
      onCleanup(() => controller.abort())
    },
    { immediate: true }
  )
  return query
})
